import { useEffect, useMemo, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";

// A one-shot, subtle crimson "unlock" burst that plays on page load.
export default function CrimsonReveal() {
  const [show, setShow] = useState(true);
  const reduce = useReducedMotion();

  const particles = useMemo(() => {
    return Array.from({ length: 30 }).map((_, i) => {
      const angle = Math.random() * Math.PI * 2;
      const dist = 100 + Math.random() * 420;
      const crimson = Math.random() > 0.35;
      return {
        id: i,
        x: Math.cos(angle) * dist,
        y: Math.sin(angle) * dist * 0.8,
        size: 3 + Math.random() * 5,
        len: crimson ? 3 + Math.random() * 3 : 1,
        delay: Math.random() * 0.18,
        rot: Math.random() * 180,
        crimson,
      };
    });
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setShow(false), 1800);
    return () => clearTimeout(t);
  }, []);

  if (reduce || !show) return null;

  return (
    <div
      data-testid="crimson-reveal"
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-[90] overflow-hidden"
    >
      {/* radial crimson flash */}
      <motion.div
        initial={{ opacity: 0.55, scale: 0.15 }}
        animate={{ opacity: 0, scale: 1.7 }}
        transition={{ duration: 1.15, ease: [0.22, 1, 0.36, 1] }}
        className="absolute left-1/2 top-[36%] h-[460px] w-[460px] -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{ background: "radial-gradient(circle, rgba(225,20,20,0.5) 0%, transparent 70%)" }}
      />
      {/* thin expanding ring */}
      <motion.div
        initial={{ opacity: 0.7, scale: 0.2 }}
        animate={{ opacity: 0, scale: 1.4 }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className="absolute left-1/2 top-[36%] h-[300px] w-[300px] -translate-x-1/2 -translate-y-1/2 rounded-full border"
        style={{ borderColor: "rgba(225,20,20,0.5)" }}
      />
      {/* particle burst */}
      <div className="absolute left-1/2 top-[36%]">
        {particles.map((p) => (
          <motion.span
            key={p.id}
            initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
            animate={{ x: p.x, y: p.y, opacity: [0, 1, 0], scale: 1 }}
            transition={{ duration: 1.3, delay: p.delay, ease: [0.22, 1, 0.36, 1] }}
            className="absolute block"
            style={{
              width: p.size,
              height: p.size * p.len,
              background: p.crimson ? "var(--noir-crimson)" : "rgba(255,255,255,0.9)",
              borderRadius: p.crimson ? "1px" : "9999px",
              transform: `rotate(${p.rot}deg)`,
            }}
          />
        ))}
      </div>
    </div>
  );
}
